import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useRequestPasswordReset } from '@zendia/frontend-shared'
import { axiosRequest } from '../../utilities/axiosClient'
import { OnboardingLayout } from '../../screens/onboarding/components'
import { Alert, AlertVariants } from '../../components/Alert'
import { Button, ButtonVariants } from '../../components/Button'
import { Input } from '../../components/Input'

export const Route = createFileRoute('/_unauthenticated/forgot-password')({
  component: ForgotPasswordComponent,
})

function ForgotPasswordComponent() {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [isSent, setIsSent] = useState(false)
  const navigate = useNavigate()
  const requestPasswordReset = useRequestPasswordReset(axiosRequest)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!/\S+@\S+\.\S+/.test(email.trim())) {
      setEmailError('Please enter a valid email address')
      return
    }
    setEmailError('')

    try {
      await requestPasswordReset.mutateAsync({ email: email.trim() })
      setIsSent(true)
    } catch {
      // Surfaced from the mutation state below
    }
  }

  // Deliberately the same confirmation whatever the server did with the address. Saying
  // "no account found" here would turn this form into a way to test which addresses are
  // registered; the mail that arrives explains either way.
  if (isSent) {
    return (
      <OnboardingLayout title="Check Your Email">
        <Alert type={AlertVariants.info} compact>
          <div className="w-full text-center">
            If that address has an account, a password reset link is on its way. If it does not, we
            have emailed you about signing up instead.
          </div>
        </Alert>
        <p className="text-sm text-center mt-6">The link expires in one hour.</p>
        <Button
          onClick={() => navigate({ to: '/login' })}
          intent={ButtonVariants.primary}
          size={ButtonVariants.large}
          className="w-full mt-6"
        >
          Back to Sign In
        </Button>
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      title="Reset Your Password"
      description="Enter the email address for your account and we'll send you a link to set a new password."
    >
      <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
        <Input
          id="email"
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          disabled={requestPasswordReset.isPending}
          error={emailError}
        />

        {requestPasswordReset.isError && (
          <div className="text-negative-700 dark:text-negative-500 text-sm py-1 px-3">
            We couldn&apos;t send the reset email. Please try again.
          </div>
        )}

        <Button
          type="submit"
          intent={ButtonVariants.primary}
          size={ButtonVariants.large}
          className="w-full"
          disabled={requestPasswordReset.isPending}
        >
          {requestPasswordReset.isPending ? 'Sending...' : 'Send Reset Link'}
        </Button>

        <div className="text-center">
          <Button
            onClick={() => navigate({ to: '/login' })}
            intent={ButtonVariants.accent}
            action={ButtonVariants.link}
            size={ButtonVariants.inlineLink}
          >
            Back to sign in
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  )
}
